import axios from 'axios';

// Every request says who is making it.
//
// The role has always been attached by hand at each call site, which is why most requests carry
// one and a few do not, and why none of them carry a user. The audit log needs a user on every
// write -- an entry that cannot say who did it is barely an entry -- and adding `user_id` to a
// hundred call sites is a hundred chances to miss one.
//
// So it is attached here, once, for every request the app makes. Existing call sites that already
// set `role` are left alone: what they pass wins, because a screen acting deliberately on behalf
// of a particular role knows better than this does.
//
// This is identification, not authentication. It is the same claim the client has always made
// about itself, sent more consistently -- the server still trusts it exactly as far as it did
// before, and a real session is still the thing that would fix that.

const IDENTITY_FIELDS = ['user_id', 'role'];

// Logging in has nobody to identify yet, and its body carries a password.
const SKIP = [/\/auth\/login$/];

const identity = () => {
    const userId = localStorage.getItem('userId');
    const role = localStorage.getItem('userRole');
    const values = {};
    if (userId !== null && userId !== '') values.user_id = userId;
    if (role !== null && role !== '') values.role = role;
    return values;
};

// Only a plain object can take extra keys. An array body is a list the server iterates, and
// FormData is a file upload -- adding a field to either changes what was sent.
const isPlainObject = (value) =>
    value !== null && typeof value === 'object' && !Array.isArray(value)
    && !(typeof FormData !== 'undefined' && value instanceof FormData)
    && !(typeof Blob !== 'undefined' && value instanceof Blob);

const attach = (target, values, alreadyInUrl = []) => {
    IDENTITY_FIELDS.forEach((field) => {
        // Never overwrite, and never duplicate. A call site that passed its own role meant it --
        // whether it passed it in `params` or wrote it into the URL by hand.
        //
        // Duplicating is not merely untidy: Express parses `?role=0&role=0` into the ARRAY
        // ["0","0"], and every reader of it does Number(), which on an array is NaN. A duplicated
        // role is therefore an unknown role, and an unknown role sees an empty menu.
        if (values[field] === undefined) return;
        if (target[field] !== undefined) return;
        if (alreadyInUrl.includes(field)) return;
        target[field] = values[field];
    });
    return target;
};

// The query keys a call site has already written into the URL string itself.
const keysInUrl = (url) => {
    const queryStart = String(url ?? '').indexOf('?');
    if (queryStart === -1) return [];
    return String(url).slice(queryStart + 1)
        .split('&')
        .map((pair) => decodeURIComponent(pair.split('=')[0]))
        .filter(Boolean);
};

const installIdentityInterceptor = () => {
    axios.interceptors.request.use((config) => {
        try {
            const url = String(config.url ?? '');
            if (SKIP.some((pattern) => pattern.test(url))) return config;

            const values = identity();
            if (Object.keys(values).length === 0) return config;

            const written = keysInUrl(url);

            // Query params for reads, so a GET does not grow a body it never had.
            config.params = attach({ ...(config.params ?? {}) }, values, written);

            // A body for the rest. DELETE carries its body in `data` here, the same as POST.
            const method = String(config.method ?? 'get').toLowerCase();
            if (method !== 'get' && method !== 'head') {
                if (config.data === undefined || config.data === null) {
                    config.data = attach({}, values, written);
                } else if (isPlainObject(config.data)) {
                    config.data = attach({ ...config.data }, values, written);
                }
            }
        } catch (error) {
            // A failure here must never stop the request going out.
            console.warn('[http] could not attach identity to request', error);
        }
        return config;
    });
};

// The four masters that decide what anybody may do. Saving one makes every cached permission
// answer wrong, including the saver's own -- and they are the person most likely to look straight
// at the result and wonder why nothing changed.
const PERMISSION_MASTERS = /\/(role_master|screen_master|department_master|user_master)\//;

// Clear the session copy after a write to one of them, so it is re-read on the next navigation
// instead of at the end of its window. Reads are left alone: a GET changes nothing.
const installPermissionInvalidation = (clearPermissions) => {
    axios.interceptors.response.use((response) => {
        try {
            const method = String(response?.config?.method ?? 'get').toLowerCase();
            const url = String(response?.config?.url ?? '');
            if (method !== 'get' && method !== 'head' && PERMISSION_MASTERS.test(url)) {
                clearPermissions();
            }
        } catch (error) {
            // Never turn a successful request into a failed one over a cache.
        }
        return response;
    });
};

export { installIdentityInterceptor, installPermissionInvalidation };
